/**
 * @fileoverview Local-first cluster registry service for system-web.
 * @module system-web/services/clusterService
 *
 * Responsibilities:
 * - Read cluster registry records from Dexie for department configuration display.
 * - Provide a reactive liveQuery stream of cluster records.
 * - Resolve the authoritative cluster name for a tenant by looking up the
 *   clusters registry by clusterId, falling back to the denormalized
 *   tenant.dataLocation.clusterName when the registry record is not yet synced.
 * - Create and update clusters through system-api (inherently-online exception
 *   — connection strings are server-only and never touch Dexie).
 * - Test a connection string before persisting (inherently-online).
 *
 * Dependencies:
 * - @firesquirrel/local-db
 * - dexie (liveQuery)
 * - ../apiClient (dynamic import for writes — inherently-online exception)
 *
 * Architecture:
 * - Local-first. UI reads from Dexie; the clusters table is hydrated by the
 *   system sync pull (server-authoritative, read-only — push is blocked).
 * - The server pull projection strips `connectionString` so credentials
 *   never reach the client.
 * - WRITES: call apiClient -> system-api route -> on success, put the
 *   response into Dexie so the UI updates immediately. The sync engine will
 *   also pull the change eventually, but the local put gives instant feedback.
 * - testConnection is inherently online and bypasses Dexie entirely.
 *
 * Notes:
 * - The denormalized tenant.dataLocation.clusterName field has historically
 *   been wrong (regex-guessed from the SRV hostname). The clusters registry
 *   `name` field is the authoritative source.
 *
 * @localFirst
 * Source of Truth: hybrid — Dexie for reads, system-api for writes.
 * Write Flow: apiClient -> Dexie put (immediate UX); sync pull reconciles eventually.
 * Sync Strategy: read-only pull (push is blocked).
 * Conflict Resolution: server-authoritative — server wins on every write.
 */
import { getSystemDb } from '@firesquirrel/local-db';
import { liveQuery, type Observable } from 'dexie';

// ---------------------------------------------------------------------------
// Contracts — mirror api/system-api/src/types/cluster.ts (without connectionString)
// ---------------------------------------------------------------------------

/**
 * @contract ClusterDoc
 * Purpose: A cluster registry document as seen by the client (no connectionString).
 * Fields: _id, name, environment, region, status, maxDatabases,
 *         currentDatabaseCount, availableSlots, notes, createdAt, updatedAt.
 * Rules: connectionString is NEVER present — stripped by the server pull projection.
 * Sync: Server-authoritative, read-only client sync.
 */
export interface ClusterDoc {
  _id: string;
  name: string;
  environment?: 'dev' | 'stage' | 'prod';
  region: string;
  status: 'active' | 'full' | 'maintenance' | 'decommissioned';
  maxDatabases: number;
  currentDatabaseCount: number;
  availableSlots?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * @contract CreateClusterInput
 * Purpose: Input for registering a new cluster.
 * Fields: name, connectionString, region, maxDatabases, notes, environment,
 *         skipConnectionTest.
 * Rules: name, connectionString, and region are required. The server tests
 *        the connection before persisting unless skipConnectionTest is true.
 * Sync: Sent to system-api POST /api/clusters; never stored in Dexie directly.
 */
export interface CreateClusterInput {
  name: string;
  /** The cluster name or SRV hostname (e.g. "dev-fire-squirrel-shard-pri").
   * The full connection string is built from this + the credentials in
   * MONGODB_ATLAS_URI on the server. Alternatively, provide connectionString
   * for a full URI override. */
  clusterHost?: string;
  /** Full MongoDB URI — overrides clusterHost if both are provided. */
  connectionString?: string;
  region: string;
  maxDatabases?: number;
  notes?: string;
  environment?: 'dev' | 'stage' | 'prod';
  skipConnectionTest?: boolean;
}

/**
 * @contract UpdateClusterInput
 * Purpose: Input for updating an existing cluster.
 * Fields: All optional — only provided fields are updated. connectionString
 *         is tested by the server before saving if included.
 * Sync: Sent to system-api PUT /api/clusters/:id; never stored in Dexie directly.
 */
export interface UpdateClusterInput {
  name?: string;
  /** The cluster name or SRV hostname — server builds the full URI. */
  clusterHost?: string;
  /** Full MongoDB URI — overrides clusterHost. */
  connectionString?: string;
  region?: string;
  maxDatabases?: number;
  status?: 'active' | 'full' | 'maintenance' | 'decommissioned';
  notes?: string;
  environment?: 'dev' | 'stage' | 'prod';
  skipConnectionTest?: boolean;
}

/**
 * @contract ConnectionTestResult
 * Purpose: Result of testing a MongoDB connection string.
 * Fields: ok, host, error (when ok is false).
 */
export interface ConnectionTestResult {
  ok: boolean;
  host: string;
  error?: string;
}

/**
 * @hook watchClusters
 *
 * Purpose:
 * - Reactive subscription to all cluster registry records in Dexie.
 *
 * Returns:
 * - Observable<any[]> — emits the full list of non-deleted cluster docs on
 *   every Dexie change.
 *
 * Side Effects:
 * - Pure Dexie read; no writes, no API calls.
 *
 * Offline Behavior:
 * - Reads from the local Dexie cache; works fully offline.
 */
export function watchClusters(): Observable<any[]> {
  return liveQuery(async () => {
    const db = await getSystemDb();
    const docs = await db.clusters.toArray();
    return docs.filter((c: any) => !c._deleted);
  });
}

/**
 * @process listClusters
 *
 * Steps:
 * 1. Read all cluster records from Dexie.
 * 2. Filter out soft-deleted records.
 *
 * Inputs:
 * - None.
 *
 * Outputs:
 * - Array of cluster registry documents (without connectionString).
 *
 * Failure Handling:
 * - Propagates Dexie read errors to the caller.
 *
 * Idempotency:
 * - REQUIRED.
 *
 * @localFirst
 *
 * Source of Truth:
 * - local (Dexie), hydrated by system sync pull.
 *
 * Write Flow:
 * - Read-only — clients never write cluster records.
 *
 * Sync Strategy:
 * - Server-authoritative; push is blocked.
 *
 * Conflict Resolution:
 * - Server wins; client cache is replaced on next pull.
 */
export async function listClusters(): Promise<any[]> {
  const db = await getSystemDb();
  const docs = await db.clusters.toArray();
  return docs.filter((c: any) => !c._deleted);
}

/**
 * @process getCluster
 *
 * Steps:
 * 1. Read a single cluster record from Dexie by its _id.
 *
 * Inputs:
 * - id: The cluster document _id (ObjectId hex string).
 *
 * Outputs:
 * - The cluster document, or null if not found.
 *
 * Failure Handling:
 * - Propagates Dexie read errors to the caller.
 *
 * Idempotency:
 * - REQUIRED.
 *
 * @localFirst
 *
 * Source of Truth:
 * - local (Dexie), hydrated by system sync pull.
 */
export async function getCluster(id: string): Promise<any | null> {
  const db = await getSystemDb();
  const doc = await db.clusters.get(id);
  return doc ?? null;
}

/**
 * @process resolveClusterName
 *
 * Steps:
 * 1. If the tenant has a dataLocation.clusterId, look up the clusters
 *    registry document by that id in Dexie.
 * 2. If found, return the registry's `name` field — the authoritative
 *    Atlas cluster name.
 * 3. If not found (e.g. the clusters table has not yet synced), fall back
 *    to the denormalized tenant.dataLocation.clusterName.
 * 4. If neither is available, return undefined.
 *
 * Inputs:
 * - tenant: A tenant document (or any object with an optional
 *   dataLocation.clusterId / dataLocation.clusterName).
 *
 * Outputs:
 * - The resolved cluster name string, or undefined.
 *
 * Failure Handling:
 * - Dexie read errors are caught and logged; falls back to the
 *   denormalized clusterName so the UI never breaks.
 *
 * Idempotency:
 * - REQUIRED.
 *
 * @localFirst
 *
 * Source of Truth:
 * - hybrid — clusters registry (local Dexie) is authoritative when
 *   available; the denormalized tenant.dataLocation.clusterName is a
 *   fallback for the period before the clusters table has synced.
 */
export async function resolveClusterName(tenant: any): Promise<string | undefined> {
  const clusterId = tenant?.dataLocation?.clusterId;
  if (clusterId) {
    try {
      const cluster = await getCluster(String(clusterId));
      if (cluster?.name) return cluster.name;
    } catch (err) {
      console.warn('[clusterService] Failed to look up cluster by clusterId, falling back to denormalized clusterName:', err);
    }
  }
  return tenant?.dataLocation?.clusterName;
}

// ---------------------------------------------------------------------------
// Write operations — inherently-online exception (connection strings are
// server-only and never touch Dexie). These go through apiClient -> system-api
// -> on success, put the response into Dexie for immediate UI feedback.
// ---------------------------------------------------------------------------

/**
 * @process createCluster
 *
 * Steps:
 * 1. Call apiClient.clusters.create with the input (including connectionString).
 * 2. On success, put the returned cluster doc (without connectionString) into
 *    Dexie for immediate UI feedback.
 *
 * Inputs:
 * - CreateClusterInput with name, connectionString, region, and optional fields.
 *
 * Outputs:
 * - ClusterDoc (without connectionString).
 *
 * Failure Handling:
 * - Propagates API errors (including connection test failures from the server).
 *
 * @localFirst
 * Source of Truth: server (server-authoritative).
 * Write Flow: apiClient POST -> Dexie put (immediate UX); sync pull reconciles eventually.
 * Sync Strategy: read-only pull; local put is for UX only.
 * Conflict Resolution: Server wins.
 */
export async function createCluster(input: CreateClusterInput): Promise<ClusterDoc> {
  const { api } = await import('../apiClient');
  const result = await api.clusters.create(input) as ClusterDoc;
  const db = await getSystemDb();
  await db.clusters.put(result);
  return result;
}

/**
 * @process updateCluster
 *
 * Steps:
 * 1. Call apiClient.clusters.update with the patch (may include connectionString).
 * 2. On success, put the returned cluster doc (without connectionString) into
 *    Dexie for immediate UI feedback.
 *
 * Inputs:
 * - id: The cluster document _id.
 * - patch: UpdateClusterInput with fields to update.
 *
 * Outputs:
 * - ClusterDoc (without connectionString).
 *
 * Failure Handling:
 * - Propagates API errors (including connection test failures from the server).
 *
 * @localFirst
 * Source of Truth: server (server-authoritative).
 * Write Flow: apiClient PUT -> Dexie put (immediate UX); sync pull reconciles eventually.
 * Sync Strategy: read-only pull; local put is for UX only.
 * Conflict Resolution: Server wins.
 */
export async function updateCluster(id: string, patch: UpdateClusterInput): Promise<ClusterDoc> {
  const { api } = await import('../apiClient');
  const result = await api.clusters.update(id, patch) as ClusterDoc;
  const db = await getSystemDb();
  await db.clusters.put(result);
  return result;
}

/**
 * @process testClusterConnection
 *
 * Purpose: Test a MongoDB connection string without persisting anything.
 *          Used by the UI's "Test Connection" button before the admin
 *          commits a new or edited cluster. This is inherently online and
 *          bypasses Dexie entirely.
 *
 * Inputs:
 * - params: { clusterHost?: string; connectionString?: string }
 *   Either clusterHost (the cluster name — server builds the full URI) or
 *   connectionString (a full MongoDB URI) must be provided.
 *
 * Outputs:
 * - ConnectionTestResult with ok, host, and optional error.
 *
 * Failure Handling:
 * - Returns a failed result; does not throw (the UI shows the error inline).
 *
 * @localFirst
 * Source of Truth: server (inherently-online — no Dexie involvement).
 */
export async function testClusterConnection(
  params: { clusterHost?: string; connectionString?: string },
): Promise<ConnectionTestResult> {
  const { api } = await import('../apiClient');
  try {
    const result = await api.clusters.testConnection(params);
    return result as ConnectionTestResult;
  } catch (err: any) {
    // The server returns a 400 with { ok: false, host, error } on connection
    // failure. apiFetch preserves that structured body on the thrown error.
    const body = err?.apiBody;
    const message = typeof body?.error === 'string'
      ? body.error
      : err instanceof Error ? err.message : 'Connection test failed';
    const host = typeof body?.host === 'string' ? body.host : '(unknown)';
    return { ok: false, host, error: message };
  }
}
